import React, {
  ReactElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { ReactComponent as Back } from 'assets/images/ic_back.svg';
import { ReactComponent as Edit } from 'assets/images/ic_edit.svg';
import { ReactComponent as AssignTraining } from 'assets/images/assign-training.svg';
import { ReactComponent as ArrowDropdown } from 'assets/images/arrow-dropdown.svg';
import { ReactComponent as ExternalLink } from 'assets/images/external-link.svg';
import { ReactComponent as TrainingSteps } from 'assets/images/training-steps.svg';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  AppFormikButtonTiles,
  AppFormikSelect,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Formik, FormikProps } from 'formik';
import { getChipColorByIndex } from 'shared/helpers/RandomChipColor';
import { TrainingPorgramsService } from 'services/TrainingProgramService';
import { Utils } from 'shared/helpers';
import Assignments from './components/Assignments';
import CreateAssignment from './components/CreateAssignment';
import { BROADCAST_MESSAGES } from 'shared/Constants';
import LinkedAISystems from './components/LinkedAISystems';
import TrainingPreviewDialog from './components/DialogPreviewTraining';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

const listValue = [
  'Overview',
  'Training Steps',
  'Assignments',
  'Linked AI Systems',
];

const tooltipList = [
  'View high-level program details, objectives, target roles, and overall progress status.',
  'Review and manage sequential modules, curriculum requirements, and specific learning activities.',
  'Track assigned users, teams, due dates, completion statuses, and individual participant progress.',
  'Identify AI models, systems, and deployment contexts associated with or governed by this training program.',
];

const ViewTraining = (): ReactElement => {
  const { trainingId } = useParams() as any;
  console.log(trainingId);
  const navigate = useNavigate();
  const [isRecordCategory, setRecordCategory] = useState<boolean>(true);
  const [trainingDetails, setTrainingDetails] = useState<any>([]);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(true);
  const [isActive, setIsActive] = useState<boolean>(true);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const formikRef = useRef<FormikProps<any> | null>(null);
  const [isLoading] = useState<boolean>(true);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);

  // Preview Training
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [quizPassed, setQuizPassed] = useState(false);
  const [, setCompletedSteps] = useState(new Set());
  const location = useLocation();

  const recordsCategory = useMemo(() => {
    const { current: formik } = formikRef;
    if (formik) {
      const recordCategoryIndex = formik.values.recordsCategory.findIndex(
        (t: boolean) => t === true
      );
      return recordCategoryIndex;
    }
    return isLoading ? -1 : 0;
  }, [isRecordCategory, isLoading]);

  const loadProgramDetails = (): void => {
    setLoadingDetails(true);
    TrainingPorgramsService.getTrainingProgramDetails(trainingId)
      .then((response) => {
        const { data } = response.data;
        setTrainingDetails(data.program_data);
        setIsActive(data.isActive);
        setLoadingDetails(false);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const handleOpen = (): void => {
    setOpen(true);
    setCurrentStep(0);
    setQuizPassed(false);
    setCompletedSteps(new Set());
  };

  const getInitialTab = (): boolean[] => {
    const tab = location.state?.activeTab;
    switch (tab) {
      case 'assessment':
        return [false, false, true, false];

      default:
        return [true, false, false, false];
    }
  };

  useLayoutEffect(() => {
    if (isRecordCategory) {
      setRecordCategory(false);
    }
  }, [isRecordCategory]);

  useEffect(() => {
    if (trainingId && recordsCategory === 0) {
      loadProgramDetails();
    }
  }, [trainingId, recordsCategory]);

  return (
    <>
      <Box className="training training-programs">
        <HeadTag title="View Training" />

        <Formik
          innerRef={formikRef}
          initialValues={{
            recordsCategory: getInitialTab(),
          }}
          onSubmit={() => console.log('test')}
        >
          {() => (
            <Box>
              <Stack spacing={4} direction="column">
                {/* Heading */}
                <Box
                  className="training-heading"
                  sx={{
                    padding: '30px 0px 0px 0px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="h4">
                      {trainingDetails?.title}
                    </Typography>
                    <Typography component="span" variant="body1">
                      {trainingDetails?.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <AppPrimaryButton
                      title="Back to Training"
                      startIcon={
                        <SvgIcon
                          component={Back}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="white"
                        />
                      }
                      onClick={() => {
                        navigate(-1);
                      }}
                    />
                    {orgACLConfig.training?.includes(
                      'training.trainingPrograms.edit'
                    ) && (
                      <AppPrimaryButton
                        title="Edit Program"
                        startIcon={
                          <SvgIcon
                            component={Edit}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="white"
                          />
                        }
                        onClick={() => {
                          navigate(`/training-programs/edit/${trainingId}`);
                        }}
                      />
                    )}
                    {orgACLConfig.training?.includes(
                      'training.trainingPrograms.assignTrainingProgram'
                    ) && (
                      <AppPrimaryButton
                        title="Assign Training"
                        startIcon={
                          <SvgIcon
                            component={AssignTraining}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="white"
                          />
                        }
                        onClick={(reload: boolean) => {
                          if (reload) {
                            window.postMessage(
                              BROADCAST_MESSAGES.RELOAD_TRAINING_ASSIGNMENTS
                            );
                          }
                          setShowAssignmentDialog(true);
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    background: 'white',
                    borderRadius: '5px',
                    padding: '10px',
                  }}
                >
                  <Chip
                    label={isActive ? 'Active' : trainingDetails?.status}
                    sx={{ background: '#06362B', color: 'white' }}
                    size="small"
                  />
                  <Typography>
                    Duration: {trainingDetails?.duration_minutes} minutes
                  </Typography>
                  <Typography>
                    Format: {trainingDetails?.format || 'Online'}
                  </Typography>
                  <Typography>Version: {trainingDetails?.version}</Typography>
                </Box>

                <Box className="solutions-menu">
                  <AppFormikButtonTiles
                    variant="outlined"
                    name="recordsCategory"
                    size="small"
                    label=""
                    disabled={false}
                    multichoice={false}
                    className="app-group-button"
                    buttons={[
                      {
                        value: listValue[0],
                        tooltip: tooltipList[0],
                        onClick: () => {
                          setRecordCategory(true);
                        },
                      },
                      {
                        value: listValue[1],
                        tooltip: tooltipList[1],
                        onClick: () => {
                          setRecordCategory(true);
                        },
                      },
                      {
                        value: listValue[2],
                        tooltip: tooltipList[2],
                        onClick: () => {
                          setRecordCategory(true);
                        },
                      },
                      {
                        value: listValue[3],
                        tooltip: tooltipList[3],
                        onClick: () => {
                          setRecordCategory(true);
                        },
                      },
                    ]}
                    outerLabel
                  />
                </Box>

                {loadingDetails && (
                  <Box sx={{ alignSelf: 'center', textAlign: 'center' }}>
                    <CircularProgress />
                  </Box>
                )}

                {!loadingDetails && recordsCategory === 0 && (
                  <Stack spacing={5}>
                    <Box>
                      <Grid
                        container
                        className="approval-array-set"
                        spacing={5}
                      >
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <Box
                            sx={{
                              background: 'white',
                              borderRadius: '5px',
                              padding: '10px',
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography
                              sx={{ fontSize: '20px', fontWeight: 600 }}
                            >
                              AI RMF Domains
                            </Typography>
                            <Box
                              sx={{
                                marginTop: '30px',
                                display: 'flex',
                                gap: '5px',
                                flexWrap: 'wrap',
                              }}
                            >
                              {trainingDetails.ai_rmf_domains &&
                                trainingDetails.ai_rmf_domains.map(
                                  (data: any, index: number) => (
                                    <Chip
                                      label={data}
                                      sx={() => {
                                        const { bg, text } =
                                          getChipColorByIndex(index);
                                        return {
                                          background: bg,
                                          color: text,
                                        };
                                      }}
                                      size="small"
                                    />
                                  )
                                )}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <Box
                            sx={{
                              background: 'white',
                              borderRadius: '5px',
                              padding: '10px',
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography
                              sx={{ fontSize: '20px', fontWeight: 600 }}
                            >
                              Target Roles
                            </Typography>
                            <Box
                              sx={{
                                marginTop: '30px',
                                display: 'flex',
                                gap: '5px',
                                flexWrap: 'wrap',
                                paddingBottom: '20px',
                              }}
                            >
                              {trainingDetails.target_roles &&
                                trainingDetails.target_roles.map(
                                  (data: any, index: number) => (
                                    <Chip
                                      label={data}
                                      sx={() => {
                                        const { bg, text } =
                                          getChipColorByIndex(index);
                                        return {
                                          background: bg,
                                          color: text,
                                        };
                                      }}
                                      size="small"
                                    />
                                  )
                                )}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <Box
                            sx={{
                              background: 'white',
                              borderRadius: '5px',
                              padding: '10px',
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography
                              sx={{ fontSize: '20px', fontWeight: 600 }}
                            >
                              Training Status
                            </Typography>
                            <Box
                              sx={{
                                marginTop: '30px',
                                paddingBottom: '20px',
                              }}
                            >
                              <Chip
                                label={
                                  trainingDetails?.isActive
                                    ? 'Active'
                                    : trainingDetails?.status
                                }
                                sx={{ background: '#06362B', color: 'white' }}
                                size="small"
                              />
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  color: '#7E7E7E',
                                  marginTop: '10px',
                                }}
                              >
                                Assignments can be created
                              </Typography>
                              <AppFormikSelect
                                name="status"
                                className="status-update"
                                notched={false}
                                labelId="fix-selection-label"
                                testid="solicitor-dropdown"
                                MenuProps={{
                                  className:
                                    'menu-fix-selection single-fix-rework',
                                }}
                                placeholder="Change Training Status"
                                notEmphasized
                                notSelectablePlaceholder={false}
                                items={['Published', 'Draft', 'Archived']}
                                IconComponentProps={ArrowDropdown}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box>
                      <Grid
                        container
                        className="approval-array-set"
                        spacing={5}
                      >
                        <Grid
                          item
                          xs={12}
                          md={6}
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <Box
                            sx={{
                              background: 'white',
                              borderRadius: '5px',
                              padding: '10px',
                              minHeight: '224px',
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography
                              sx={{ fontSize: '20px', fontWeight: 600 }}
                            >
                              Prerequisites
                            </Typography>
                            <Box
                              sx={{
                                marginTop: '30px',
                              }}
                            >
                              {trainingDetails?.prerequisites.length > 0
                                ? trainingDetails?.prerequisites.map(
                                    (data: any) => (
                                      <Typography
                                        sx={{
                                          fontSize: '18px',
                                          color: '#7E7E7E',
                                        }}
                                        mt={3}
                                      >
                                        • {data}
                                      </Typography>
                                    )
                                  )
                                : '-'}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={6}
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <Box
                            sx={{
                              background: 'white',
                              borderRadius: '5px',
                              padding: '10px',
                              minHeight: '224px',
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography
                              sx={{ fontSize: '20px', fontWeight: 600 }}
                            >
                              Learning Objectives
                            </Typography>
                            <Box
                              sx={{
                                marginTop: '30px',
                              }}
                            >
                              {trainingDetails?.learning_objectives.length > 0
                                ? trainingDetails?.learning_objectives.map(
                                    (data: any) => (
                                      <Typography
                                        sx={{
                                          fontSize: '18px',
                                          color: '#7E7E7E',
                                        }}
                                        mt={3}
                                      >
                                        • {data}
                                      </Typography>
                                    )
                                  )
                                : '-'}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {trainingDetails?.external_urls.length > 0 && (
                      <Box
                        sx={{
                          background: 'white',
                          borderRadius: '5px',
                          padding: '10px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '20px',
                            fontWeight: 600,
                          }}
                        >
                          <ExternalLink /> External Training Links
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: '18px',
                            color: '#7E7E7E',
                            marginTop: '20px',
                          }}
                        >
                          Provider: {trainingDetails.external_provider}
                        </Typography>

                        {trainingDetails?.external_urls.map((ex: any) => (
                          <Box
                            sx={{
                              background: '#F1F5F4',
                              padding: '10px',
                              borderRadius: '5px',
                              marginTop: '20px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '18px',
                              }}
                            >
                              {ex.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '18px',
                                color: '#7E7E7E',
                                marginTop: '20px',
                              }}
                            >
                              {ex.url}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Box
                      sx={{
                        background: 'white',
                        borderRadius: '5px',
                        padding: '10px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '20px',
                          fontWeight: 600,
                          marginBottom: '20px',
                        }}
                      >
                        Recurrence and Certification
                      </Typography>

                      <Grid container className="approval-array-set">
                        <Grid item xs={12} md={6}>
                          <Typography
                            sx={{
                              fontSize: '20px',
                              fontWeight: 600,
                            }}
                          >
                            Training Recurrence
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: '20px',
                              marginTop: '10px',
                              color: '#7E7E7E',
                            }}
                          >
                            {trainingDetails?.recurrence_months
                              ? trainingDetails?.recurrence_months
                              : 'One-time training'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography
                            sx={{
                              fontSize: '20px',
                              fontWeight: 600,
                            }}
                          >
                            Certification Validity
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: '20px',
                              marginTop: '10px',
                              color: '#7E7E7E',
                            }}
                          >
                            {trainingDetails?.certification_valid_months
                              ? trainingDetails?.recurrence_months
                              : 'No expiration'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                )}

                {!loadingDetails && recordsCategory === 1 && (
                  <Box
                    sx={{
                      background: 'white',
                      padding: '10px',
                      borderRadius: '5px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      <TrainingSteps />
                      <Typography sx={{ fontSize: '20px' }}>
                        {listValue[parseFloat(recordsCategory)]}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '18px',
                          color: '#7E7E7E',
                          marginTop: '20px',
                        }}
                      >
                        This training has{' '}
                        {trainingDetails?.training_definition?.steps.length}
                        {trainingDetails?.training_definition?.steps.length < 2
                          ? ' Step'
                          : ' Steps'}
                        :
                      </Typography>
                      <AppPrimaryButton
                        onClick={handleOpen}
                        className="btn-small"
                        title="Preview Training"
                        startIcon={
                          <SvgIcon
                            component={PlayArrowIcon}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        }
                        sx={{ margin: '0 0 10px 0' }}
                      />
                    </Box>

                    {trainingDetails?.training_definition?.steps.map(
                      (data: any, index: number) => (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: '#f1f5f4',
                            borderRadius: '5px',
                            border: '1px solid lightgray',
                            padding: '10px 20px',
                          }}
                          mt={5}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                            }}
                          >
                            <Typography>{index + 1}.</Typography>
                            <Typography>{data.title}</Typography>
                          </Box>

                          <Chip
                            label={data.type}
                            size="small"
                            sx={{
                              background:
                                data.type === 'video' ? '#06362B' : 'white',
                              color:
                                data.type === 'video' ? 'white' : '#06362B',
                              border: '1px solid lightgray',
                            }}
                          />
                        </Box>
                      )
                    )}
                  </Box>
                )}

                {!loadingDetails && recordsCategory === 2 && <Assignments />}
                {!loadingDetails && recordsCategory === 3 && (
                  <LinkedAISystems
                    trainingId={trainingId}
                    linkedSystems={trainingDetails.aisystemId}
                    reloadDetail={() => {
                      loadProgramDetails();
                    }}
                  />
                )}
              </Stack>
            </Box>
          )}
        </Formik>
        {showAssignmentDialog && (
          <CreateAssignment
            trainingId={`${trainingId}`}
            openDialog={showAssignmentDialog}
            onClose={(status) => {
              if (status) {
                window.postMessage(
                  BROADCAST_MESSAGES.RELOAD_TRAINING_ASSIGNMENTS
                );
              }
              setShowAssignmentDialog(false);
              console.log(status);
            }}
          />
        )}
      </Box>

      {trainingDetails?.training_definition?.steps?.length > 0 && (
        <TrainingPreviewDialog
          open={open}
          setOpen={setOpen}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          quizPassed={quizPassed}
          setQuizPassed={setQuizPassed}
          setCompletedSteps={setCompletedSteps}
          steps={trainingDetails?.training_definition?.steps}
        />
      )}
    </>
  );
};

export default ViewTraining;
